/**
 * An image edit (plan 3.3, 7.3; the WP4 and WP5 notes are the contract),
 * shared by the lightboxes and the chat's image block:
 *
 * - `POST image-edits` answers 202 with version N+1 pending; the artifact
 *   stays ready at the old head until it settles.
 * - The edit is running while that version is pending and is not the
 *   artifact's `currentVersion` yet. It settles on the `artifact:update`
 *   nudge (or a 4 second poll as the fallback): ready moves the head (the
 *   image shows the new version), failed keeps the old head and the
 *   version's error says why.
 * - An edit the agent started (`editImage` in chat) is followed the same
 *   way from the tool result's `versionId`.
 */
import { useCallback, useEffect, useState } from 'react';
import { requestArtifactImageEdit } from '@/lib/artifact-edits-api';
import { useArtifactDetail, useArtifactSocketNudge } from '@/hooks/useArtifact';
import { useArtifactVersion, useInvalidateArtifact } from '@/hooks/useArtifactVersions';

export const IMAGE_EDIT_POLL_MS = 4_000;

const SHOWN_ERROR_CODES = new Set([
  'ARTIFACT_EDIT_INVALID',
  'ARTIFACT_LIMIT_REACHED',
  'SUBSCRIPTION_ENTITLEMENT_DENIED',
  'IMAGE_DISABLED',
]);

export type ImageEditState =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'editing'; versionId: string }
  | { status: 'failed'; message: string | null }
  | { status: 'done' };

/** Whether the version is still being made: pending, and not the head yet. */
export function isImageEditRunning(input: {
  versionId: string | null | undefined;
  headVersionId: string | null | undefined;
  versionStatus: 'pending' | 'ready' | 'failed' | null | undefined;
}): boolean {
  if (!input.versionId) return false;
  if (input.headVersionId === input.versionId) return false;
  return input.versionStatus !== 'ready' && input.versionStatus !== 'failed';
}

export function useImageEditController(
  artifactId: string | null,
  options: {
    /** An edit already running (the agent's `editImage` result). */
    followVersionId?: string | null;
    enabled?: boolean;
  } = {},
) {
  const enabled = Boolean(artifactId) && (options.enabled ?? true);
  const detail = useArtifactDetail(artifactId, { enabled });
  const invalidate = useInvalidateArtifact();
  const [state, setState] = useState<ImageEditState>({ status: 'idle' });
  const followed = options.followVersionId ?? null;
  const watchedId = state.status === 'editing' ? state.versionId : followed;
  const headId = detail.data?.currentVersion?.id ?? null;
  const version = useArtifactVersion(artifactId, watchedId, {
    enabled: enabled && Boolean(watchedId) && watchedId !== headId,
    refetchInterval: IMAGE_EDIT_POLL_MS,
  });
  const versionStatus = version.data?.status ?? null;
  const running = isImageEditRunning({ versionId: watchedId, headVersionId: headId, versionStatus });

  const { refetch: refetchVersion } = version;
  const nudged = useCallback(() => {
    void refetchVersion();
    if (artifactId) invalidate(artifactId);
  }, [artifactId, invalidate, refetchVersion]);
  useArtifactSocketNudge(artifactId, enabled && running, nudged);

  // Settled: the head moved (ready) or the version failed.
  useEffect(() => {
    if (!watchedId || !artifactId) return;
    if (versionStatus === 'ready' && headId !== watchedId) {
      invalidate(artifactId);
      return;
    }
    if (state.status !== 'editing') return;
    if (headId === watchedId) setState({ status: 'done' });
    else if (versionStatus === 'failed') {
      setState({ status: 'failed', message: version.data?.error?.message ?? null });
    }
  }, [artifactId, headId, invalidate, state.status, version.data?.error?.message, versionStatus, watchedId]);

  const submit = useCallback(
    async (instruction: string): Promise<boolean> => {
      const text = instruction.trim();
      if (!artifactId || !text) return false;
      setState({ status: 'sending' });
      try {
        const result = await requestArtifactImageEdit(artifactId, {
          ...(headId ? { baseVersionId: headId } : {}),
          instruction: text.slice(0, 2_000),
        });
        setState({ status: 'editing', versionId: result.version.id });
        invalidate(artifactId);
        return true;
      } catch (error) {
        const failure = error as { code?: unknown; message?: unknown } | null;
        const code = typeof failure?.code === 'string' ? failure.code : '';
        // The server's words are meant for the user for these codes only.
        const message =
          SHOWN_ERROR_CODES.has(code) && typeof failure?.message === 'string' ? failure.message : null;
        setState({ status: 'failed', message });
        return false;
      }
    },
    [artifactId, headId, invalidate],
  );

  const reset = useCallback(() => setState({ status: 'idle' }), []);

  return {
    detail,
    state,
    /** An edit is running (this one, or the agent's). */
    running: running || state.status === 'sending',
    /** The failed version's words (the model refused, or a newer version won). */
    failure:
      state.status === 'failed'
        ? { message: state.message }
        : followed && versionStatus === 'failed' && headId !== followed
          ? { message: version.data?.error?.message ?? null }
          : null,
    submit,
    reset,
  };
}
